import React from 'react';
import PropTypes from 'prop-types';
import './Button.scss';

/**
 * Design System Button Component
 *
 * A flexible button component with support for different variants, sizes,
 * loading states, and custom hover colors.
 *
 * Uses forwardRef to support Tooltip, Popconfirm, and other wrapper components
 * that need to attach refs for positioning.
 *
 * @example
 * <Button variant="primary" hoverColor="#fad133" onClick={handleClick}>
 *   Save Changes
 * </Button>
 */
const Button = React.forwardRef(({
    variant = 'primary',
    size = 'medium',
    loading = false,
    disabled = false,
    icon,
    hoverColor,
    uppercase = true,
    children,
    onClick,
    className = '',
    style = {},
    ...props
}, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const baseClassName = 'ds-button';
    const variantClassName = `${baseClassName}--${variant}`;
    const sizeClassName = `${baseClassName}--${size}`;
    const loadingClassName = loading ? `${baseClassName}--loading` : '';
    const disabledClassName = (disabled || loading) ? `${baseClassName}--disabled` : '';
    const uppercaseClassName = uppercase ? `${baseClassName}--uppercase` : '';

    const combinedClassName = [
        baseClassName,
        variantClassName,
        sizeClassName,
        loadingClassName,
        disabledClassName,
        uppercaseClassName,
        className
    ].filter(Boolean).join(' ');

    // Apply custom hover color if provided
    const buttonStyle = {
        ...style,
        ...(isHovered && hoverColor && !disabled && !loading ? {
            backgroundColor: hoverColor,
            borderColor: hoverColor
        } : {})
    };

    const handleClick = (e) => {
        if (disabled || loading) {
            e.preventDefault();
            return;
        }
        if (onClick) {
            onClick(e);
        }
    };

    return (
        <button
            ref={ref}
            className={combinedClassName}
            style={buttonStyle}
            onClick={handleClick}
            disabled={disabled || loading}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            {...props}
        >
            {loading && (
                <span className="ds-button__spinner" data-testid="ds-button-spinner">
                    <span className="ds-button__spinner-icon" />
                </span>
            )}
            {icon && !loading && (
                <span className="ds-button__icon" data-testid="ds-button-icon">{icon}</span>
            )}
            {children && (
                <span className="ds-button__text" data-testid="ds-button-text">{children}</span>
            )}
        </button>
    );
});

// Add display name for better debugging with forwardRef
Button.displayName = 'Button';

Button.propTypes = {
    /** Button variant: primary (black), secondary (outline), danger (red) */
    variant: PropTypes.oneOf(['primary', 'secondary', 'danger']),

    /** Button size */
    size: PropTypes.oneOf(['small', 'medium', 'large']),

    /** Loading state - shows spinner and disables interaction */
    loading: PropTypes.bool,

    /** Disabled state */
    disabled: PropTypes.bool,

    /** Icon element to display */
    icon: PropTypes.node,

    /** Custom hover background color (e.g., '#fad133', '#FD3635') */
    hoverColor: PropTypes.string,

    /** Transform text to uppercase */
    uppercase: PropTypes.bool,

    /** Button content */
    children: PropTypes.node,

    /** Click handler */
    onClick: PropTypes.func,

    /** Additional CSS classes */
    className: PropTypes.string,

    /** Inline styles */
    style: PropTypes.object
};

export default Button;
